/* @HEADER
 * @version 1.1.894 | 2026-10-24
 * @purpose Brings a board's section into view when the space page draws it, by observing the page rather than waiting on a timer, once, and not after the caller stops it.
 * @sidecar focusBoardSection.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8265, ZVX-DEV-7134

/**
 * Brings the section of a board into view once the space page has drawn it (ZVX-4464, a board link). The page marks each
 * section's header `data-board-id`; this waits for it by observing the page, not by a timer, and gives up only when the
 * caller does (the returned function), which the caller does when the person moves on. A board the page never draws leaves
 * the person on its space, which is where the link brought them.
 */

export function focusBoardSection(boardId: string, root: ParentNode = document): () => void {
  // The attribute is compared, not selected: a board id is opaque and may hold any character a selector would need escaped.
  const bring = (): boolean => {
    const section = Array.from(root.querySelectorAll('[data-board-id]')).find(
      (candidate) => candidate.getAttribute('data-board-id') === boardId,
    );
    if (!section) return false;
    section.scrollIntoView({ block: 'start' });
    return true;
  };
  if (bring()) return () => {};
  const watched = root instanceof Document ? root.documentElement : (root as Element);
  const observer = new MutationObserver(() => {
    if (bring()) observer.disconnect();
  });
  observer.observe(watched, { childList: true, subtree: true });
  return () => observer.disconnect();
}
